import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSurvey } from "../context/SurveyContext";
import { useAuth } from "../context/AuthContext";
import { Question, ConditionalLogic } from "../types/Survey";
import { v4 as uuidv4 } from "uuid";
import ConditionalLogicBuilder from "./ConditionalLogicBuilder";
import SurveyPreviewModal from "./SurveyPreviewModal";
import { getTemplateById } from "../data/templateData";
import { renderCategoryIcon } from "../utils/categoryIcons";
import {
  Shuffle,
  Edit,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import "./SurveyBuilder.css";
import { getBackendUrl } from "../config/env";

const SurveyBuilder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: surveyId } = useParams<{ id: string }>();
  const { addSurvey, updateSurvey } = useSurvey();
  const { user, centerInfo, selectedCenterId } = useAuth();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isPublished, setIsPublished] = useState(false);
  const [publishStartDate, setPublishStartDate] = useState<string>("");
  const [publishEndDate, setPublishEndDate] = useState<string>("");
  const [showConditionalLogic, setShowConditionalLogic] = useState<{
    [questionId: string]: boolean;
  }>({});
  const [showPreview, setShowPreview] = useState(false);
  const [movingQuestions, setMovingQuestions] = useState<{
    [questionId: string]: "up" | "down" | null;
  }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [categories, setCategories] = useState<any[]>([]);
  const [hasResponses, setHasResponses] = useState(false);
  const [responseCount, setResponseCount] = useState(0);

  // 카테고리 목록 로드
  useEffect(() => {
    loadCategories();
  }, [selectedCenterId]); // selectedCenterId가 변경될 때마다 재조회

  // 편집 모드일 때 설문조사 데이터 로드
  useEffect(() => {
    if (surveyId) {
      loadSurveyData();
    }
  }, [surveyId]);

  // URL에서 템플릿 ID를 확인하고 템플릿 로드
  useEffect(() => {
    if (!surveyId) {
      // 편집 모드가 아닐 때만 템플릿 로드
      const urlParams = new URLSearchParams(location.search);
      const templateId = urlParams.get("template");

      if (templateId) {
        loadTemplateData(templateId);
      }
    }
  }, [location.search, surveyId]);

  const loadTemplateData = async (templateId: string) => {
    try {
      console.log("Loading template with ID:", templateId);
      // 백엔드에서 템플릿 데이터 가져오기
      const response = await fetch(
        `${getBackendUrl()}/api/templates/${templateId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const template = await response.json();
        console.log("Template loaded:", template);

        // 템플릿 데이터로 설문 초기화
        setTitle(template.name);
        setDescription(template.description);
        setCategoryId(template.categoryId || template.category || "");

        // 질문들의 ID를 새로 생성하여 충돌 방지
        const idMapping: { [oldId: string]: string } = {};
        const questionsWithNewIds = template.questions.map((q: any) => {
          const newId = uuidv4();
          idMapping[q.id] = newId;
          return {
            ...q,
            id: newId,
          };
        });
        
        // 조건부 로직의 ID도 업데이트
        const questionsWithUpdatedLogic = questionsWithNewIds.map((q: any) => {
          if (q.conditionalLogic && q.conditionalLogic.length > 0) {
            return {
              ...q,
              conditionalLogic: q.conditionalLogic.map((logic: any) => ({
                ...logic,
                sourceQuestionId: idMapping[logic.sourceQuestionId] || logic.sourceQuestionId,
                targetQuestionId: logic.targetQuestionId ? 
                  (idMapping[logic.targetQuestionId] || logic.targetQuestionId) : 
                  undefined
              }))
            };
          }
          return q;
        });

        console.log("Questions to set:", questionsWithUpdatedLogic);
        setQuestions(questionsWithUpdatedLogic);

        // URL에서 template 파라미터 제거
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      } else {
        console.error("Failed to load template, status:", response.status);
        // 백엔드에서 실패하면 mockData에서 시도
        const template = getTemplateById(templateId);
        if (template) {
          setTitle(template.name);
          setDescription(template.description);
          setCategoryId(template.category || "");

          const idMapping: { [oldId: string]: string } = {};
          const questionsWithNewIds = template.questions.map((q) => {
            const newId = uuidv4();
            idMapping[q.id] = newId;
            return {
              ...q,
              id: newId,
            };
          });
          
          // 조건부 로직의 ID도 업데이트
          const questionsWithUpdatedLogic = questionsWithNewIds.map((q: any) => {
            if (q.conditionalLogic && q.conditionalLogic.length > 0) {
              return {
                ...q,
                conditionalLogic: q.conditionalLogic.map((logic: any) => ({
                  ...logic,
                  sourceQuestionId: idMapping[logic.sourceQuestionId] || logic.sourceQuestionId,
                  targetQuestionId: logic.targetQuestionId ? 
                    (idMapping[logic.targetQuestionId] || logic.targetQuestionId) : 
                    undefined
                }))
              };
            }
            return q;
          });

          setQuestions(questionsWithUpdatedLogic);

          const newUrl = window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        }
      }
    } catch (error) {
      console.error("Error loading template:", error);
      // 오류 발생 시 mockData에서 시도
      const template = getTemplateById(templateId);
      if (template) {
        setTitle(template.name);
        setDescription(template.description);
        setCategoryId(template.category || "");

        const idMapping: { [oldId: string]: string } = {};
        const questionsWithNewIds = template.questions.map((q) => {
          const newId = uuidv4();
          idMapping[q.id] = newId;
          return {
            ...q,
            id: newId,
          };
        });
        
        // 조건부 로직의 ID도 업데이트
        const questionsWithUpdatedLogic = questionsWithNewIds.map((q: any) => {
          if (q.conditionalLogic && q.conditionalLogic.length > 0) {
            return {
              ...q,
              conditionalLogic: q.conditionalLogic.map((logic: any) => ({
                ...logic,
                sourceQuestionId: idMapping[logic.sourceQuestionId] || logic.sourceQuestionId,
                targetQuestionId: logic.targetQuestionId ? 
                  (idMapping[logic.targetQuestionId] || logic.targetQuestionId) : 
                  undefined
              }))
            };
          }
          return q;
        });

        setQuestions(questionsWithUpdatedLogic);

        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  };

  const loadCategories = async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const url = centerUserId
        ? `${getBackendUrl()}/api/survey-categories?isActive=true&centerUserId=${centerUserId}`
                  : `${getBackendUrl()}/api/survey-categories?isActive=true`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const checkResponses = async () => {
    if (!surveyId) return;

    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const url = centerUserId
        ? `${getBackendUrl()}/api/responses/survey/${surveyId}?centerUserId=${centerUserId}`
        : `${getBackendUrl()}/api/responses/survey/${surveyId}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const count = data.length;
        setResponseCount(count);
        setHasResponses(count > 0);
      }
    } catch (error) {
      console.error("Error checking responses:", error);
    }
  };

  const loadSurveyData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${getBackendUrl()}/api/surveys/${surveyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("설문조사를 불러오는데 실패했습니다");
      }

      const survey = await response.json();
      setTitle(survey.title || "");
      setDescription(survey.description || "");
      // Sort questions by orderIndex to maintain correct order
      const sortedQuestions = (survey.questions || []).sort(
        (a: any, b: any) => (a.orderIndex || 0) - (b.orderIndex || 0)
      );
      setQuestions(sortedQuestions);
      setCategoryId(survey.categoryId || "");
      setIsPublished(
        survey.status === "published" || survey.isPublished || false
      );

      // Set publication dates if they exist
      if (survey.publishStartDate) {
        const startDate = new Date(survey.publishStartDate);
        setPublishStartDate(startDate.toISOString().slice(0, 16));
      }
      if (survey.publishEndDate) {
        const endDate = new Date(survey.publishEndDate);
        setPublishEndDate(endDate.toISOString().slice(0, 16));
      }

      // 응답 수 확인
      if (survey.status === "published" || survey.isPublished) {
        await checkResponses();
      }
    } catch (error) {
      console.error("Error loading survey:", error);
      alert("설문조사를 불러오는데 실패했습니다");
      navigate("/survey-list");
    } finally {
      setIsLoading(false);
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: uuidv4(),
      type: "multiple-choice",
      question: "",
      options: ["옵션 1", "옵션 2"],
      required: false,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === id) {
          const updatedQuestion = { ...q, ...updates };

          // 질문 타입 변경 시 기본값 설정
          if (updates.type) {
            switch (updates.type) {
              case "multiple-choice":
              case "checkbox":
              case "dropdown":
                if (!updatedQuestion.options) {
                  updatedQuestion.options = ["옵션 1", "옵션 2"];
                }
                break;
              case "rating":
                if (!updatedQuestion.minRating) updatedQuestion.minRating = 1;
                if (!updatedQuestion.maxRating) updatedQuestion.maxRating = 5;
                break;
              case "matrix":
                if (!updatedQuestion.matrixRows)
                  updatedQuestion.matrixRows = ["행 1", "행 2"];
                if (!updatedQuestion.matrixColumns)
                  updatedQuestion.matrixColumns = ["열 1", "열 2"];
                if (!updatedQuestion.matrixType)
                  updatedQuestion.matrixType = "radio";
                break;
              case "ranking":
                if (!updatedQuestion.rankingItems)
                  updatedQuestion.rankingItems = ["항목 1", "항목 2", "항목 3"];
                break;
              case "file-upload":
                if (!updatedQuestion.allowedFileTypes)
                  updatedQuestion.allowedFileTypes = [];
                if (!updatedQuestion.maxFileSize)
                  updatedQuestion.maxFileSize = 10;
                break;
            }
          }

          return updatedQuestion;
        }
        return q;
      })
    );
  };

  const deleteQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const moveQuestionUp = (index: number) => {
    // Can't move first question or second question (to keep first question fixed)
    if (index <= 1) return;

    // Don't swap with first question (index 0)
    if (index === 1) return;

    const questionId = questions[index].id;
    const prevQuestionId = questions[index - 1].id;

    // Set animation states
    setMovingQuestions({
      [questionId]: "up",
      [prevQuestionId]: "down",
    });

    // Perform the swap after a short delay to show animation
    setTimeout(() => {
      const newQuestions = [...questions];
      [newQuestions[index - 1], newQuestions[index]] = [
        newQuestions[index],
        newQuestions[index - 1],
      ];
      setQuestions(newQuestions);

      // Clear animation states
      setTimeout(() => {
        setMovingQuestions({});
      }, 300);
    }, 50);
  };

  const moveQuestionDown = (index: number) => {
    // Can't move first question or last question down
    if (index === 0 || index >= questions.length - 1) return;

    // Don't allow moving to position 0 (swapping with first question)
    // This means index 1 (second question) can only swap with index 2, not with index 0
    const targetIndex = index + 1;

    const questionId = questions[index].id;
    const nextQuestionId = questions[targetIndex].id;

    // Set animation states
    setMovingQuestions({
      [questionId]: "down",
      [nextQuestionId]: "up",
    });

    // Perform the swap after a short delay to show animation
    setTimeout(() => {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[targetIndex]] = [
        newQuestions[targetIndex],
        newQuestions[index],
      ];
      setQuestions(newQuestions);

      // Clear animation states
      setTimeout(() => {
        setMovingQuestions({});
      }, 300);
    }, 50);
  };

  const addOption = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options) {
      const newOptions = [
        ...question.options,
        `옵션 ${question.options.length + 1}`,
      ];
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const updateOption = (
    questionId: string,
    optionIndex: number,
    value: string
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options) {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const deleteOption = (questionId: string, optionIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options && question.options.length > 2) {
      const newOptions = question.options.filter(
        (_, index) => index !== optionIndex
      );
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const addRankingItem = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.rankingItems) {
      const newItems = [
        ...question.rankingItems,
        `항목 ${question.rankingItems.length + 1}`,
      ];
      updateQuestion(questionId, { rankingItems: newItems });
    }
  };

  const updateRankingItem = (
    questionId: string,
    itemIndex: number,
    value: string
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.rankingItems) {
      const newItems = [...question.rankingItems];
      newItems[itemIndex] = value;
      updateQuestion(questionId, { rankingItems: newItems });
    }
  };

  const deleteRankingItem = (questionId: string, itemIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.rankingItems && question.rankingItems.length > 2) {
      const newItems = question.rankingItems.filter(
        (_, index) => index !== itemIndex
      );
      updateQuestion(questionId, { rankingItems: newItems });
    }
  };

  const addMatrixRow = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixRows) {
      const newRows = [
        ...question.matrixRows,
        `행 ${question.matrixRows.length + 1}`,
      ];
      updateQuestion(questionId, { matrixRows: newRows });
    }
  };

  const updateMatrixRow = (
    questionId: string,
    rowIndex: number,
    value: string
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixRows) {
      const newRows = [...question.matrixRows];
      newRows[rowIndex] = value;
      updateQuestion(questionId, { matrixRows: newRows });
    }
  };

  const deleteMatrixRow = (questionId: string, rowIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixRows && question.matrixRows.length > 1) {
      const newRows = question.matrixRows.filter(
        (_, index) => index !== rowIndex
      );
      updateQuestion(questionId, { matrixRows: newRows });
    }
  };

  const addMatrixColumn = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixColumns) {
      const newColumns = [
        ...question.matrixColumns,
        `열 ${question.matrixColumns.length + 1}`,
      ];
      updateQuestion(questionId, { matrixColumns: newColumns });
    }
  };

  const updateMatrixColumn = (
    questionId: string,
    columnIndex: number,
    value: string
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixColumns) {
      const newColumns = [...question.matrixColumns];
      newColumns[columnIndex] = value;
      updateQuestion(questionId, { matrixColumns: newColumns });
    }
  };

  const deleteMatrixColumn = (questionId: string, columnIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.matrixColumns && question.matrixColumns.length > 1) {
      const newColumns = question.matrixColumns.filter(
        (_, index) => index !== columnIndex
      );
      updateQuestion(questionId, { matrixColumns: newColumns });
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert("제목을 입력해주세요");
      return;
    }

    if (!categoryId) {
      alert("카테고리를 선택해주세요");
      return;
    }

    if (questions.length === 0) {
      alert("최소 1개 이상의 질문을 추가해주세요");
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("로그인이 필요합니다. 로그인 페이지로 이동합니다.");
        navigate("/login");
        return;
      }

      const surveyData = {
        title,
        description,
        categoryId,
        questions: questions.map((q, index) => ({
          ...q,
          type: q.type,
          question: q.question,
          options: q.options || [],
          required: q.required || false,
          conditionalLogic: q.conditionalLogic || [],
          minRating: q.minRating,
          maxRating: q.maxRating,
          matrixRows: q.matrixRows || [],
          matrixColumns: q.matrixColumns || [],
          matrixType: q.matrixType,
          rankingItems: q.rankingItems || [],
          allowedFileTypes: q.allowedFileTypes || [],
          maxFileSize: q.maxFileSize,
          orderIndex: index, // Add order index to maintain question order
        })),
        isPublished,
        status: isPublished ? "published" : "draft",
        publishStartDate: publishStartDate || null,
        publishEndDate: publishEndDate || null,
        createdById: String(user?.userid || user?.email || "external-user"),
        centerId: user?.centerId || centerInfo?.centerId || null,
        centerUserId:
          selectedCenterId || user?.centerId || centerInfo?.centerId || null,
      };

      const url = surveyId
        ? `${getBackendUrl()}/api/surveys/${surveyId}`
        : `${getBackendUrl()}/api/surveys`;

      const method = surveyId ? "PATCH" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(surveyData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "설문조사 저장에 실패했습니다");
      }

      const savedSurvey = await response.json();

      // Context에도 추가/업데이트 (로컬 상태 동기화)
      if (surveyId && updateSurvey) {
        updateSurvey(surveyId, {
          title,
          description,
          questions,
          isPublished,
        });
      } else {
        addSurvey({
          title,
          description,
          questions,
          isPublished,
        });
      }

      alert(
        surveyId
          ? "설문조사가 성공적으로 수정되었습니다!"
          : "설문조사가 성공적으로 저장되었습니다!"
      );
      navigate("/survey-list");
    } catch (error) {
      console.error("Error saving survey:", error);
      alert(
        error instanceof Error
          ? error.message
          : "설문조사 저장 중 오류가 발생했습니다"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="survey-builder">
        <div className="loading-state">
          <p>설문조사를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="survey-list-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {surveyId ? (
              <>
                <Edit size={32} />
                설문조사 수정
              </>
            ) : (
              <>
                <Plus size={32} />새 설문조사 만들기
              </>
            )}
          </h1>
          <p className="page-description">
            {surveyId
              ? "기존 설문조사를 수정하고 관리하세요"
              : "새로운 설문조사를 만들어 배포하세요"}
          </p>
        </div>
        <div className="flex gap-4">
          <Button variant="secondary" onClick={() => navigate("/survey-list")}>
            취소
          </Button>
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            미리보기
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || (hasResponses && isPublished)}
            title={
              hasResponses && isPublished
                ? "응답이 있는 설문조사는 수정할 수 없습니다"
                : ""
            }
          >
            {isSaving ? "저장 중..." : surveyId ? "수정" : "저장"}
          </Button>
        </div>
      </div>

      {hasResponses && isPublished && (
        <div className="flex items-center gap-3 p-4 mb-6 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
          <span className="text-xl">⚠️</span>
          <div>
            <div className="font-semibold">수정 불가:</div>
            <div className="text-sm">
              이 설문조사에는 {responseCount}개의 응답이 있어 수정할 수
              없습니다.
              <br />
              게시된 설문조사는 응답이 있으면 데이터 일관성을 위해 수정이
              제한됩니다.
            </div>
          </div>
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="p-6">
          <Input
            type="text"
            placeholder="설문조사 제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={hasResponses && isPublished}
            className="mb-4 text-lg"
          />
          <div className="flex gap-4 mb-4">
            <Select
              value={categoryId}
              onValueChange={setCategoryId}
              disabled={hasResponses && isPublished}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="카테고리 선택" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <span className="flex items-center gap-2">
                      {renderCategoryIcon(category.icon)} {category.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="설문조사 설명 (선택사항)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={hasResponses && isPublished}
            className="min-h-[80px]"
          />
        </CardContent>
      </Card>

      <div
        className={`space-y-6 ${
          hasResponses && isPublished ? "opacity-70 pointer-events-none" : ""
        }`}
      >
        {questions.map((question, index) => (
          <Card
            key={question.id}
            className={`mb-6 transition-all duration-300 ease-in-out ${
              movingQuestions[question.id] === "up"
                ? "question-card-moving-up"
                : movingQuestions[question.id] === "down"
                ? "question-card-moving-down"
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <Badge variant="secondary">질문 {index + 1}</Badge>
              <div className="flex gap-2">
                {/* 순서 변경 버튼 - 첫 번째 질문은 제외 */}
                {index > 0 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveQuestionUp(index)}
                      disabled={index === 1}
                      title="위로 이동"
                      className="hover:bg-gray-100"
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveQuestionDown(index)}
                      disabled={index === questions.length - 1}
                      title="아래로 이동"
                      className="hover:bg-gray-100"
                    >
                      <ChevronDown size={16} />
                    </Button>
                  </>
                )}
                {/* 첫 번째 질문이 아니고, 질문이 2개 이상일 때만 조건부 로직 버튼 표시 */}
                {index > 0 && questions.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowConditionalLogic((prev) => ({
                        ...prev,
                        [question.id]: !prev[question.id],
                      }));
                    }}
                    title="조건부 로직 설정"
                    className={
                      question.conditionalLogic &&
                      question.conditionalLogic.length > 0
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : ""
                    }
                  >
                    <Shuffle size={16} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteQuestion(question.id)}
                  title="질문 삭제"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Input
                type="text"
                placeholder="질문을 입력하세요"
                value={question.question}
                onChange={(e) =>
                  updateQuestion(question.id, { question: e.target.value })
                }
                className="mb-4"
              />

              <div className="flex items-center gap-4 mb-4">
                <Select
                  value={question.type}
                  onValueChange={(value) =>
                    updateQuestion(question.id, {
                      type: value as Question["type"],
                    })
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple-choice">객관식</SelectItem>
                    <SelectItem value="checkbox">체크박스</SelectItem>
                    <SelectItem value="text">주관식</SelectItem>
                    <SelectItem value="rating">평점</SelectItem>
                    <SelectItem value="dropdown">드롭다운</SelectItem>
                    {/* <SelectItem value="matrix">매트릭스</SelectItem> */}
                    {/* <SelectItem value="ranking">순위 매기기</SelectItem> */}
                    {/* <SelectItem value="file-upload">파일 업로드</SelectItem> */}
                  </SelectContent>
                </Select>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`required-${question.id}`}
                    checked={question.required}
                    onCheckedChange={(checked) =>
                      updateQuestion(question.id, { required: !!checked })
                    }
                  />
                  <Label htmlFor={`required-${question.id}`}>필수 응답</Label>
                </div>
              </div>

              {(question.type === "multiple-choice" ||
                question.type === "checkbox" ||
                question.type === "dropdown") && (
                <div className="space-y-2">
                  {question.options?.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={option}
                        onChange={(e) =>
                          updateOption(question.id, optionIndex, e.target.value)
                        }
                        className="flex-1"
                      />
                      {question.options!.length > 2 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteOption(question.id, optionIndex)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addOption(question.id)}
                    className="mt-2"
                  >
                    옵션 추가
                  </Button>
                </div>
              )}

              {question.type === "rating" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>최소값</Label>
                    <Input
                      type="number"
                      value={question.minRating || 1}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          minRating: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>최대값</Label>
                    <Input
                      type="number"
                      value={question.maxRating || 5}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          maxRating: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {question.type === "matrix" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>매트릭스 타입</Label>
                    <Select
                      value={question.matrixType || "radio"}
                      onValueChange={(value) =>
                        updateQuestion(question.id, {
                          matrixType: value as
                            | "radio"
                            | "checkbox"
                            | "dropdown",
                        })
                      }
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="radio">라디오 버튼</SelectItem>
                        <SelectItem value="checkbox">체크박스</SelectItem>
                        <SelectItem value="dropdown">드롭다운</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>행 (질문 항목들)</Label>
                    <div className="space-y-2">
                      {question.matrixRows?.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex items-center gap-2">
                          <Input
                            type="text"
                            value={row}
                            onChange={(e) =>
                              updateMatrixRow(
                                question.id,
                                rowIndex,
                                e.target.value
                              )
                            }
                            className="flex-1"
                          />
                          {question.matrixRows!.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                deleteMatrixRow(question.id, rowIndex)
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addMatrixRow(question.id)}
                      >
                        행 추가
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>열 (답변 선택지들)</Label>
                    <div className="space-y-2">
                      {question.matrixColumns?.map((column, columnIndex) => (
                        <div
                          key={columnIndex}
                          className="flex items-center gap-2"
                        >
                          <Input
                            type="text"
                            value={column}
                            onChange={(e) =>
                              updateMatrixColumn(
                                question.id,
                                columnIndex,
                                e.target.value
                              )
                            }
                            className="flex-1"
                          />
                          {question.matrixColumns!.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                deleteMatrixColumn(question.id, columnIndex)
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addMatrixColumn(question.id)}
                      >
                        열 추가
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {question.type === "ranking" && (
                <div className="space-y-2">
                  <Label>순위를 매길 항목들</Label>
                  <div className="space-y-2">
                    {question.rankingItems?.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={item}
                          onChange={(e) =>
                            updateRankingItem(
                              question.id,
                              itemIndex,
                              e.target.value
                            )
                          }
                          className="flex-1"
                        />
                        {question.rankingItems!.length > 2 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              deleteRankingItem(question.id, itemIndex)
                            }
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addRankingItem(question.id)}
                    >
                      항목 추가
                    </Button>
                  </div>
                </div>
              )}

              {question.type === "file-upload" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>허용 파일 형식</Label>
                    <Input
                      type="text"
                      placeholder="예: .jpg,.png,.pdf"
                      value={question.allowedFileTypes?.join(",") || ""}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          allowedFileTypes: e.target.value
                            .split(",")
                            .map((type) => type.trim())
                            .filter((type) => type),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>최대 파일 크기 (MB)</Label>
                    <Input
                      type="number"
                      value={question.maxFileSize || 10}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          maxFileSize: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {showConditionalLogic[question.id] && (
                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-sm">조건부 로직 설정</h4>
                    {question.conditionalLogic &&
                      question.conditionalLogic.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            updateQuestion(question.id, {
                              conditionalLogic: [],
                            });
                            setShowConditionalLogic((prev) => ({
                              ...prev,
                              [question.id]: false,
                            }));
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} className="mr-1" />
                          모든 조건 삭제
                        </Button>
                      )}
                  </div>
                  <ConditionalLogicBuilder
                    question={question}
                    allQuestions={questions}
                    onLogicChange={(logic) =>
                      updateQuestion(question.id, { conditionalLogic: logic })
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <Button
          variant="secondary"
          onClick={addQuestion}
          disabled={hasResponses && isPublished}
          className="w-full"
        >
          <Plus size={16} className="mr-2" />
          질문 추가
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">게시 설정</h3>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="publish"
              checked={isPublished}
              onCheckedChange={(checked) => setIsPublished(!!checked)}
              disabled={hasResponses && isPublished}
            />
            <Label htmlFor="publish">바로 게시하기</Label>
          </div>

          {isPublished && (
            <div className="space-y-4 mt-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-900">
              <div className="space-y-2">
                <Label htmlFor="publishStartDate">게시 시작일</Label>
                <Input
                  id="publishStartDate"
                  type="datetime-local"
                  value={publishStartDate}
                  onChange={(e) => setPublishStartDate(e.target.value)}
                  disabled={hasResponses && isPublished}
                  className="w-full"
                />
                <p className="text-sm text-gray-500">
                  설정하지 않으면 즉시 게시됩니다
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="publishEndDate">게시 종료일</Label>
                <Input
                  id="publishEndDate"
                  type="datetime-local"
                  value={publishEndDate}
                  onChange={(e) => {
                    const endDate = e.target.value;
                    if (
                      publishStartDate &&
                      endDate &&
                      new Date(endDate) <= new Date(publishStartDate)
                    ) {
                      alert("종료일은 시작일보다 이후여야 합니다");
                      return;
                    }
                    setPublishEndDate(endDate);
                  }}
                  min={publishStartDate}
                  disabled={hasResponses && isPublished}
                  className="w-full"
                />
                <p className="text-sm text-gray-500">
                  설정하지 않으면 무기한 게시됩니다
                </p>
              </div>

              {publishStartDate && publishEndDate && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    게시 기간:{" "}
                    {new Date(publishStartDate).toLocaleString("ko-KR")} ~{" "}
                    {new Date(publishEndDate).toLocaleString("ko-KR")}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <SurveyPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        survey={{
          title: title || "제목 없음",
          description: description || "",
          questions: questions,
        }}
        isTemplate={false}
      />
    </div>
  );
};

export default SurveyBuilder;
